import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ComboBox } from '@/components/ComboBox';
import { useToast } from '@/components/Toast';
import {
  useClient, useUpdateClient, useLookup, useClientGroupings, useClientPicker, type ClientPickerRow,
} from '@/api/hooks';
import { api, ApiError } from '@/api/client';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const US_CA = ['United States', 'Canada'];
// State/province lists per country (verbatim from Power Apps ComboBox_State), same as New Client.
const US_STATES = ['AK','AL','AR','AZ','CA','CO','CT','DC','DE','FL','GA','HI','IA','ID','IL','IN','KS','KY','LA','MA','MD','ME','MI','MN','MO','MS','MT','NC','ND','NE','NH','NJ','NM','NV','NY','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VA','VT','WA','WI','WV','WY'];
const CA_PROVINCES = ['AB','BC','MB','NB','NL','NS','NT','NU','ON','PE','QC','SK','YT'];

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const EIN1_RE = /^\d{3}-\d{2}-\d{4}$|^\d{2}-\d{7}$/;
const EIN2_RE = /^\d{3}-\d{2}-\d{4}$/;

// state key -> record column, for initializing the edit form from the client record.
const INIT_MAP: Record<string, string> = {
  clientName: 'ClientName', clientEmail: 'ClientEmail', secondaryEmail: 'SecondaryEmail',
  contactName: 'ContactName', mailingAddress1: 'MailingAddress1', mailingAddress2: 'MailingAddress2',
  city: 'City', state: 'State', zipCode: 'ZipCode', phone: 'Phone', secondaryPhone: 'SecondaryPhone',
  taxSSN1: 'TaxSSN1', taxSSN2: 'TaxSSN2', referralSource: 'ReferralSource',
  surveyEmail: 'SurveyEmail', surveyAttention: 'SurveyAttention',
  clientManager: 'ClientManager', entityType: 'EntityType', brcIndustry: 'BRCIndustry',
  naics: 'NAICS', fiscalYearEndMonth: 'FiscalYearEndMonth', country: 'Country',
  billingStatementEmail: 'BillingStatementEmail',
};

export function ClientEdit() {
  const { clientId, subId } = useParams();
  const navigate = useNavigate();
  const notify = useToast();
  const rec = useClient(clientId, subId);
  const r = rec.data;
  const update = useUpdateClient(clientId ?? '', subId ?? '');
  const [editMode, setEditMode] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [f, setF] = useState<Record<string, string>>({});
  const set = (k: string, v: string) => setF((s) => ({ ...s, [k]: v }));

  // Billing Client ID cascade (only when Bill To = Other) + survey toggle, mirrors New Client.
  const [billingGrouping, setBillingGrouping] = useState('');
  const [selectedBilling, setSelectedBilling] = useState<ClientPickerRow | null>(null);
  const [survey, setSurvey] = useState(false);

  const managers = useLookup('client-managers');
  const industries = useLookup('brc-industries');
  const naics = useLookup('naics');
  const entityTypes = useLookup('entity-types');
  const countries = useLookup('countries');
  const groupings = useClientGroupings();
  const billingPicker = useClientPicker(
    f.billingClientType === 'Other' ? billingGrouping : undefined,
    true,
  );
  // View-only Engagement Location, derived from the selected Client Manager.
  const engLocation = useQuery({
    queryKey: ['eng-location', f.clientManager ?? ''],
    queryFn: () => api.get<{ EngLocation: string }[]>(`/lookups/engagement-locations?partner=${encodeURIComponent(f.clientManager ?? '')}`),
    enabled: !!f.clientManager,
  });

  useEffect(() => {
    if (!r) return;
    const init: Record<string, string> = {};
    for (const [sk, rk] of Object.entries(INIT_MAP)) init[sk] = String(r[rk] ?? '');
    init.billingClientType = String(r.BillingClientType ?? 'Self');
    init.billingClientId = String(r.BillingClientID ?? '');
    // Show the existing note (Power App binds Note to selectedClientItem.Note).
    // In edit mode it stays editable, pre-filled — matching the Power App's Default.
    init.note = String(r.Note ?? '');
    setF(init);
    setSurvey(!!String(r.SurveyEmail ?? '').trim());
  }, [r]);

  if (rec.isFetching && !r) return <div className="p-6">Loading…</div>;
  if (!r) return <div className="p-6">Client not found.</div>;

  const opType = r.CompletionDate ? 'Complete' : 'Pending';
  const clientType = String(r.ClientType ?? 'Master');
  const isMaster = String(r.ClientSubID ?? '0') === '0'; // master sub id is 0
  const billOptions = clientType === 'Master' ? ['Self', 'Other'] : ['Self', 'Master', 'Other'];
  const country = f.country ?? '';
  const needsState = US_CA.includes(country);
  const stateItems = country === 'United States' ? US_STATES : country === 'Canada' ? CA_PROVINCES : [];

  const badEmail = (v?: string) => !!v && !EMAIL_RE.test(v);
  const clientEmailBad = badEmail(f.clientEmail);
  const secEmailBad = badEmail(f.secondaryEmail);
  const surveyEmailBad = badEmail(f.surveyEmail);
  const billStmtEmailBad = badEmail(f.billingStatementEmail);
  const ein1Bad = !!f.taxSSN1 && !EIN1_RE.test(f.taxSSN1);
  const ein2Bad = !!f.taxSSN2 && !EIN2_RE.test(f.taxSSN2);

  function changeCountry(v: string) {
    setF((s) => ({ ...s, country: v, state: '' })); // Country OnChange → Reset(State)
  }

  async function save() {
    setConfirm(false);
    if (!f.note?.trim()) { notify('error', 'Note is required when saving a change.'); return; }
    if (!f.billingClientType) { notify('error', 'Bill To is required.'); return; }
    if (f.billingClientType === 'Other' && !f.billingClientId?.trim()) {
      notify('error', 'Billing Client is required when Bill To is "Other".'); return;
    }
    if (!f.billingStatementEmail?.trim()) { notify('error', 'Billing Statement Email is required.'); return; }
    if (clientEmailBad || secEmailBad || surveyEmailBad || billStmtEmailBad || ein1Bad || ein2Bad) {
      notify('error', 'Please correct the highlighted fields before saving.'); return;
    }
    const body = { ...f, clientId, clientSubId: subId, clientType };
    try {
      await update.mutateAsync({ body, clientType, opType });
      notify('success', 'Client change submitted (new pending request created).');
      navigate('/clients');
    } catch (e) {
      notify('error', e instanceof ApiError ? e.problem.title : 'Save failed');
    }
  }

  const D = !editMode; // disabled shorthand

  return (
    <div className="w-full space-y-4 p-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-primary">{String(r.ClientName || `Client ${r.ClientID}`)}</h2>
          <p className="text-xs text-muted-foreground">
            {isMaster
              ? `Master Client · ID ${String(r.ClientID)}`
              : `Sub Client · ID ${String(r.ClientID)} / Sub ${String(r.ClientSubID)}`}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to={`/clients/${clientId}/${subId}/additional`} className="btn-secondary">Additional Details</Link>
          {!editMode ? <button className="btn-primary" onClick={() => setEditMode(true)}>Edit Details</button> : (
            <>
              <button className="btn-secondary" onClick={() => { setEditMode(false); rec.refetch(); }}>Cancel</button>
              <button className="btn-primary" disabled={update.isPending} onClick={() => setConfirm(true)}>Save</button>
            </>
          )}
        </div>
      </div>
      {editMode && <div className="rounded-md border border-warning/40 bg-warning/10 p-2 text-xs">Saving creates a <b>new Pending Request</b>. <b>Note is required.</b></div>}

      <div className="grid gap-4 xl:grid-cols-2 xl:items-start">
        <Section title="Client Information">
          <Field label="Client ID"><input className="input" value={String(r.ClientID)} disabled /></Field>
          <Field label="Client Sub ID"><input className="input" value={String(r.ClientSubID)} disabled /></Field>
          <Field label="Client Type"><input className="input" value={clientType} disabled /></Field>

          <Field label="Client Name *"><input className="input" disabled={D} value={f.clientName ?? ''} onChange={(e) => set('clientName', e.target.value)} /></Field>
          <Field label={<>Client Email * <Warn show={clientEmailBad} tip="Enter a valid email" /></>}>
            <input className="input" disabled={D} value={f.clientEmail ?? ''} onChange={(e) => set('clientEmail', e.target.value)} />
          </Field>
          <Field label={<>Secondary Email <Warn show={secEmailBad} tip="Enter a valid email" /></>}>
            <input className="input" disabled={D} value={f.secondaryEmail ?? ''} onChange={(e) => set('secondaryEmail', e.target.value)} />
          </Field>

          <Field label="Bill To *">
            <select className="input" disabled={D} value={f.billingClientType ?? ''}
              onChange={(e) => { set('billingClientType', e.target.value); setSelectedBilling(null); }}>
              <option value="">— select —</option>
              {billOptions.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>
          <Field label={`Billing Client starts with ${f.billingClientType === 'Other' ? '*' : ''}`}>
            <select className="input" disabled={D || f.billingClientType !== 'Other'} value={billingGrouping}
              onChange={(e) => { setBillingGrouping(e.target.value); setSelectedBilling(null); }}>
              <option value="">— select —</option>
              {(groupings.data ?? []).map((g) => (
                <option key={String(g.ClientGrouping)} value={String(g.ClientGrouping)}>{String(g.ClientGrouping)}</option>
              ))}
            </select>
          </Field>
          <Field label={`Billing Client ID ${f.billingClientType === 'Other' ? '*' : ''}`}>
            {editMode && f.billingClientType === 'Other' ? (
              <ComboBox<ClientPickerRow>
                items={billingPicker.data ?? []}
                value={selectedBilling}
                onChange={(c) => { setSelectedBilling(c); set('billingClientId', c?.ClientID ?? ''); }}
                getLabel={(c) => c.ClientFullName ?? `${c.ClientID}-${c.ClientName}`}
                getKey={(c) => c.ClientFullName ?? `${c.ClientID}-${c.ClientName}`}
                placeholder={f.billingClientId ? `Current: ${f.billingClientId}` : 'Search by ID or name…'}
                disabled={!billingGrouping}
                loading={billingPicker.isFetching}
              />
            ) : (
              // View mode, or Bill To = Self/Master (derived, not user-picked):
              // show the stored Billing Client ID as a readable value.
              <input className="input" disabled value={f.billingClientId ?? ''} />
            )}
          </Field>
          <Field label={<>Billing Statement Email * <Warn show={billStmtEmailBad} tip="Enter a valid email" /></>}>
            <input className="input" disabled={D} value={f.billingStatementEmail ?? ''} onChange={(e) => set('billingStatementEmail', e.target.value)} />
          </Field>
        </Section>

        <Section title="Contact Details">
          <Field label="Contact Name *"><input className="input" disabled={D} value={f.contactName ?? ''} onChange={(e) => set('contactName', e.target.value)} /></Field>
          <Field label="Mailing Address 1 *"><input className="input" disabled={D} value={f.mailingAddress1 ?? ''} onChange={(e) => set('mailingAddress1', e.target.value)} /></Field>
          <Field label="Mailing Address 2"><input className="input" disabled={D} value={f.mailingAddress2 ?? ''} onChange={(e) => set('mailingAddress2', e.target.value)} /></Field>
          <Field label="City *"><input className="input" disabled={D} value={f.city ?? ''} onChange={(e) => set('city', e.target.value)} /></Field>
          <Field label="Country *">
            <LookupCombo data={countries.data} field="Value" value={country} disabled={D}
              onChange={changeCountry} placeholder="Select country…" />
          </Field>
          <Field label={`State/Province ${needsState ? '*' : ''}`}>
            <ComboBox<string>
              items={stateItems}
              value={f.state ? f.state : null}
              onChange={(s) => set('state', s ?? '')}
              getLabel={(s) => s}
              getKey={(s) => s}
              placeholder="Select state…"
              disabled={D || !needsState}
            />
          </Field>
          <Field label="Zip/Postal Code *"><input className="input" disabled={D} value={f.zipCode ?? ''} onChange={(e) => set('zipCode', e.target.value)} /></Field>
          <Field label="Phone *"><input className="input" disabled={D} value={f.phone ?? ''} onChange={(e) => set('phone', e.target.value)} /></Field>
          <Field label="Secondary Phone"><input className="input" disabled={D} value={f.secondaryPhone ?? ''} onChange={(e) => set('secondaryPhone', e.target.value)} /></Field>
        </Section>

        <Section title="Other Details">
          <Field label="Client Manager *">
            <LookupCombo data={managers.data} field="ClientManager" value={f.clientManager ?? ''} disabled={D}
              onChange={(v) => set('clientManager', v)} placeholder="Select manager…" />
          </Field>
          <Field label="Engagement Location">
            <input className="input" value={String(engLocation.data?.[0]?.EngLocation ?? '')} disabled placeholder="Set by Client Manager" />
          </Field>
          <Field label="Entity Type *">
            <LookupCombo data={entityTypes.data} field="EntityType" value={f.entityType ?? ''} disabled={D}
              onChange={(v) => set('entityType', v)} placeholder="Select entity type…" />
          </Field>
          <Field label="BRC Industry *">
            <LookupCombo data={industries.data} field="Industry" value={f.brcIndustry ?? ''} disabled={D}
              onChange={(v) => set('brcIndustry', v)} placeholder="Select industry…" />
          </Field>
          <Field label="NAICS *">
            <LookupCombo data={naics.data} field="NAICS" value={f.naics ?? ''} disabled={D}
              onChange={(v) => set('naics', v)} placeholder="Search NAICS…" />
          </Field>
          <Field label="Fiscal Year End Month *">
            <select className="input" disabled={D} value={f.fiscalYearEndMonth ?? ''} onChange={(e) => set('fiscalYearEndMonth', e.target.value)}>
              <option value="">— select —</option>
              {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>
          <Field label={<>Tax EIN / SSN1 <Warn show={ein1Bad} tip="Format: XX-XXXXXXX or XXX-XX-XXXX" /></>}>
            <input className="input" maxLength={11} placeholder="00-0000000" disabled={D} value={f.taxSSN1 ?? ''} onChange={(e) => set('taxSSN1', e.target.value)} />
          </Field>
          <Field label={<>Tax SSN2 <Warn show={ein2Bad} tip="Format: XXX-XX-XXXX" /></>}>
            <input className="input" maxLength={11} placeholder="000-00-0000" disabled={D} value={f.taxSSN2 ?? ''} onChange={(e) => set('taxSSN2', e.target.value)} />
          </Field>
          <Field label="Referral Source"><input className="input" disabled={D} value={f.referralSource ?? ''} onChange={(e) => set('referralSource', e.target.value)} /></Field>
        </Section>

        <Section title="Survey">
          <div className="col-span-full flex items-center gap-2">
            <input id="surveyChk" type="checkbox" disabled={D} checked={survey}
              onChange={(e) => {
                setSurvey(e.target.checked);
                if (!e.target.checked) setF((s) => ({ ...s, surveyEmail: '', surveyAttention: '' }));
              }} />
            <label htmlFor="surveyChk" className="text-sm font-semibold text-foreground">Survey</label>
            <span className="text-xs text-muted-foreground" title="Check if different than contact above.">ⓘ</span>
          </div>
          <Field label={<>Survey Email <Warn show={surveyEmailBad} tip="Enter a valid email" /></>}>
            <input className="input" disabled={D || !survey} value={f.surveyEmail ?? ''} onChange={(e) => set('surveyEmail', e.target.value)} />
          </Field>
          <Field label="Survey Attention">
            <input className="input" disabled={D || !survey} value={f.surveyAttention ?? ''} onChange={(e) => set('surveyAttention', e.target.value)} />
          </Field>
        </Section>
      </div>

      <div>
        <label className="label">Note {editMode ? '*' : ''}</label>
        <textarea className="input" rows={3} disabled={D} value={f.note ?? ''} onChange={(e) => set('note', e.target.value)} />
      </div>

      {editMode && <p className="text-xs font-semibold text-accent">*  Mandatory Fields</p>}

      <ConfirmDialog open={confirm}
        message="Do you wish to submit the client form changes? Press 'Yes' to continue, 'No' to cancel."
        onConfirm={save} onCancel={() => setConfirm(false)} />
    </div>
  );
}

/** ComboBox bound to a lookup table by a single display column (stores the string). */
function LookupCombo({ data, field, value, onChange, placeholder, disabled }: {
  data?: Record<string, unknown>[];
  field: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const rows = Array.isArray(data) ? data : [];
  const selected = rows.find((r) => String(r[field] ?? '') === value) ?? null;
  return (
    <ComboBox
      items={rows}
      value={selected}
      onChange={(r) => onChange(r ? String(r[field] ?? '') : '')}
      getLabel={(r) => String(r[field] ?? '')}
      getKey={(r) => String(r[field] ?? '')}
      placeholder={placeholder}
      disabled={disabled}
    />
  );
}

function Warn({ show, tip }: { show: boolean; tip: string }) {
  if (!show) return null;
  return <span title={tip} className="ml-1 font-bold text-orange-500">!</span>;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <h3 className="mb-2 border-b border-border pb-2 text-sm font-semibold uppercase tracking-wide text-primary">{title}</h3>
      <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">{children}</div>
    </div>
  );
}

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
    </div>
  );
}
